const API_BASE = import.meta.env.VITE_API_URL || '';
const GRAPHQL_PUBLIC_URL = `${API_BASE}/public`;

export async function exchangeOidcToken(idToken: string): Promise<{ accessToken: string; refreshToken: string }> {
  const res = await fetch(GRAPHQL_PUBLIC_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      query: `
        mutation oidc_token_exchange($data: OidcTokenExchangeInputType!) {
          oidc_token_exchange(data: $data) {
            hasError
            message
            data
          }
        }
      `,
      variables: {
        data: { id_token: idToken },
      },
    }),
  });

  const json = await res.json();
  const result = json?.data?.oidc_token_exchange;

  if (result?.hasError) {
    throw new Error(result.message || 'Token exchange failed');
  }

  return {
    accessToken: result.data.accessToken,
    refreshToken: result.data.refreshToken,
  };
}

export function setAuthCookie(accessToken: string) {
  document.cookie = `accessToken=${accessToken}; path=/; max-age=${60 * 60 * 24}; SameSite=Lax`;
}

// The refresh token outlives the access token (30d server-side) so an expired
// access token can be renewed without sending the user back through OIDC.
export function setRefreshCookie(refreshToken: string) {
  document.cookie = `refreshToken=${refreshToken}; path=/; max-age=${60 * 60 * 24 * 30}; SameSite=Lax`;
}

export function clearAuthCookie() {
  document.cookie = 'accessToken=; path=/; max-age=0';
  document.cookie = 'refreshToken=; path=/; max-age=0';
}

function readCookie(name: string): string | null {
  const cookie = document.cookie.split('; ').find((c) => c.startsWith(`${name}=`));
  return cookie ? cookie.split('=')[1] : null;
}

export function getAccessToken(): string | null {
  return readCookie('accessToken');
}

export function getRefreshToken(): string | null {
  return readCookie('refreshToken');
}

/**
 * Exchanges the stored refresh token for a fresh access token and persists both.
 * Returns the new access token, or null when there is nothing to refresh with or
 * the server rejects the token (revoked/expired) — callers should then re-login.
 *
 * Concurrent callers share one in-flight request: a page that fires several
 * requests at once must not burn the refresh token N times, since each refresh
 * rotates it server-side and the losers would send an already-rotated token.
 */
let inFlightRefresh: Promise<string | null> | null = null;

export function refreshAccessToken(): Promise<string | null> {
  if (inFlightRefresh) return inFlightRefresh;

  const refreshToken = getRefreshToken();
  if (!refreshToken) return Promise.resolve(null);

  const run = (async () => {
    try {
      const res = await fetch(GRAPHQL_PUBLIC_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: `
            mutation refresh_token($data: RefreshTokenInputType!) {
              refresh_token(data: $data) {
                hasError
                message
                data
              }
            }
          `,
          variables: { data: { refresh_token: refreshToken } },
        }),
      });

      const json = await res.json();
      const result = json?.data?.refresh_token;
      if (result?.hasError || !result?.data?.accessToken) {
        clearAuthCookie();
        return null;
      }

      setAuthCookie(result.data.accessToken);
      if (result.data.refreshToken) setRefreshCookie(result.data.refreshToken);
      return result.data.accessToken as string;
    } catch {
      // Network failure is not proof the token is bad — leave cookies alone so a
      // transient blip doesn't log the user out.
      return null;
    }
  })();

  // Cleared outside the async body: an early return there would run before the
  // assignment below and pin a stale resolved promise here forever.
  inFlightRefresh = run.finally(() => {
    inFlightRefresh = null;
  });

  return inFlightRefresh;
}

export function isAuthenticated(): boolean {
  return !!getAccessToken();
}

export function isEmbedded(): boolean {
  return typeof window !== 'undefined' && window.parent !== window;
}

/**
 * Origin of the page embedding us, or null when we're not framed / it can't be
 * determined. Used both as the postMessage target and to vet replies.
 */
function getParentOrigin(): string | null {
  try {
    const ancestors = window.location.ancestorOrigins;
    if (ancestors?.length) return ancestors[0];
  } catch {
    /* not supported outside WebKit — fall through to the referrer */
  }
  try {
    if (document.referrer) return new URL(document.referrer).origin;
  } catch {
    /* malformed referrer */
  }
  return null;
}

export const TOKEN_REQUEST = 'tbit-soluagro-token-request';
export const TOKEN_REPLY = 'tbit-soluagro-token';
export const SESSION_EXPIRED_EVENT = 'tbit-session-expired';

/**
 * Asks the embedding page for its current access token.
 *
 * The embed only ever receives a token through the URL hash, captured once when
 * the iframe mounts. The host refreshes its own token in the background, so a
 * long-lived embed ends up holding a stale copy with no way to renew it. This
 * asks the host for a fresh one instead of reloading the frame.
 *
 * Resolves null if we're not embedded, the host doesn't answer, or it has
 * nothing to give.
 */
export function requestTokenFromParent(timeoutMs = 3000): Promise<string | null> {
  if (!isEmbedded()) return Promise.resolve(null);

  const parentOrigin = getParentOrigin();
  // Without a known origin we'd have to post to '*', broadcasting a request the
  // wrong frame could answer. Not worth it — bail and let the caller degrade.
  if (!parentOrigin) return Promise.resolve(null);

  return new Promise((resolve) => {
    let done = false;

    const finish = (token: string | null) => {
      if (done) return;
      done = true;
      window.removeEventListener('message', onMessage);
      clearTimeout(timer);
      resolve(token);
    };

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== parentOrigin) return;
      if (event.data?.type !== TOKEN_REPLY) return;
      const token = event.data.token;
      finish(typeof token === 'string' && token ? token : null);
    };

    const timer = setTimeout(() => finish(null), timeoutMs);
    window.addEventListener('message', onMessage);

    try {
      window.parent.postMessage({ type: TOKEN_REQUEST }, parentOrigin);
    } catch {
      finish(null);
    }
  });
}

/**
 * Obtains a usable access token after one was rejected, trying every avenue the
 * current context offers: the refresh token (standalone) then the host frame
 * (embed). Returns null when the session is truly unrecoverable.
 *
 * `staleToken` is the token that just failed — the host may hand back the very
 * same one, and replaying the request with it would only 401 again.
 */
export async function renewSession(staleToken?: string | null): Promise<string | null> {
  const viaRefreshToken = await refreshAccessToken();
  if (viaRefreshToken) return viaRefreshToken;

  const fromParent = await requestTokenFromParent();
  if (fromParent && fromParent !== staleToken) {
    setAuthCookie(fromParent);
    return fromParent;
  }

  return null;
}

/**
 * Announces that the session is beyond recovery so the UI can offer a way out
 * instead of surfacing a bare API error.
 */
export function notifySessionExpired() {
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
}
